import { Button, FormField, Modal, Select, Skeleton, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { sozlesmeAdaylariSorgusu } from "../lib/sharedQueries";
import { UyeListesiDurumu } from "./UyeListesiDurumu";

/**
 * GÖREV YENİDEN ATAMA DİYALOĞU (CLOS-233 `[170]` · alt görev CLOS-968).
 *
 * ### ⚠ Uç vardı, arayüz yoktu
 *
 * `reassignContractReviewTask`, `reassignContractApprovalTask` ve
 * `reassignSignatureRequestSigner` sunucuda çalışıyordu; hiçbir ekranda karşılığı yoktu.
 * Yani izinli kullanıcı, izne çıkan bir inceleyicinin görevini devredemiyor, süreci
 * tıkanmış bırakıyordu. Kartın istediği "uygun atanan listesi" ile "çakışma davranışı"
 * burada birleşiyor.
 *
 * ### ⚠ Aday listesi "aktif üye", DAHA FAZLASI DEĞİL
 *
 * Aday havuzu `GET /api/tenants/:id/members`'ın aktif satırları — inceleme/onay başlatma
 * ekranlarının kullandığı aynı kaynak. Listeyi burada başka bir kurala göre daraltmak,
 * ekranın sunucudan farklı bir uygunluk tanımı taşıması demekti; sunucu zaten kendi
 * kapılarını uyguluyor (üyelik, çakışma, görev durumu).
 *
 * ⚠ Liste boş gelirse SEBEP YAZILIR: `/members` `organization.member` yetkisi istiyor;
 * yetkisi olmayan kullanıcıda sorgu düşer ve boş liste "kimse yok" gibi görünürdü
 * (`UyeListesiDurumu` bu ayrım için var).
 *
 * ### ⚠ ÇAKIŞMA sunucunun kararı, burada TAHMİN EDİLMİYOR
 *
 * Sunucu üç ayrı çakışma döndürüyor: akış kapanmış, görev zaten sonuçlanmış, görev zaten
 * bu kullanıcıda. Bunları istemcide önceden hesaplamaya kalksaydık iki gerçek olurdu ve
 * ekranın gerçeği listeyi çektiği andan itibaren bayatlardı. Diyalog isteği gönderir,
 * sunucunun cümlesini olduğu gibi gösterir ve AÇIK KALIR — kullanıcı başka bir aday
 * seçebilsin diye. Kapansaydı, hata mesajını okuyan kişi baştan başlamak zorunda kalırdı.
 *
 * ### ⚠ Gerekçe kuralı `config/workflowCancelReassign.json`'dan gelir
 *
 * Onay ve imza yeniden atamasında gerekçe ZORUNLU (karar hiyerarşisini değiştiriyor),
 * incelemede değil. Kural bileşende uydurulmuyor; çağıran ekran taşıyor, asıl kapı
 * sunucudaki `applyReasonPolicy`. İstemci sınırı yalnız kullanıcıyı boşuna reddettirmemek
 * için önden gösteriyor.
 */
export interface TaskReassignDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** Modal başlığı — hangi görevin devredildiği yazar. */
  title: string;
  /** Aday seçim alanının etiketi ("Yeni inceleyici", "Yeni onaylayıcı", "Yeni imzacı"). */
  assigneeLabel: string;
  /** `true` ise gerekçe olmadan gönderilemez (`config/workflowCancelReassign.json`). */
  reasonRequired?: boolean;
  /** Sunucudaki asgari uzunluk. */
  reasonMinLength?: number;
  /**
   * Görevin ŞU ANKİ sorumlusu — listeden çıkarılır. Sunucu bu seçimi zaten
   * `"Görev zaten bu kullanıcıya atanmış"` diye reddediyor; kullanıcıyı o reddi almaya
   * göndermenin bir faydası yok.
   */
  currentAssigneeUserId?: string | null;
  /**
   * Aday listesinin bağlamı (Mega E2E BUG-002).
   *
   * ⚠ Liste artık organizasyon üye dizininden değil SÖZLEŞMEDEN okunuyor: dizinin kapısı
   * `organization.member` ve o bir yönetim yetkisi — görevi devredebilen ama organizasyonu
   * yönetmeyen kullanıcı seçiciyi boş görüyordu. Bu diyaloğun üç çağıranı da (inceleme,
   * onay, imza) bir sözleşme yüzeyi; bağlam her zaman var.
   */
  contractId: string;
  /** İsteği gönderen fonksiyon; çakışmayı ÇAĞIRAN değil bu diyalog gösterir. */
  reassign: (input: { userId: string; reason?: string }) => Promise<unknown>;
  /** Başarıdan sonra listeleri tazeleyen çağrı. */
  onDone: () => Promise<void> | void;
  successMessage: string;
  fallbackErrorMessage: string;
}

export function TaskReassignDialog({
  open,
  onClose,
  tenantId,
  contractId,
  title,
  assigneeLabel,
  reasonRequired = false,
  reasonMinLength = 10,
  currentAssigneeUserId,
  reassign,
  onDone,
  successMessage,
  fallbackErrorMessage,
}: TaskReassignDialogProps) {
  const toast = useToast();
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");
  const [cakisma, setCakisma] = useState<string | null>(null);

  const uyeler = useQuery({
    enabled: open,
    ...sozlesmeAdaylariSorgusu(tenantId, contractId),
  });

  // Her açılış temiz başlar: önceki denemenin adayı ve gerekçesi başka bir göreve taşınmaz.
  useEffect(() => {
    if (!open) return;
    setUserId("");
    setReason("");
    setCakisma(null);
  }, [open]);

  /** ⚠ Aktiflik süzgeci sunucuda (BUG-002); burada yalnız mevcut sorumlu düşülüyor. */
  const adaylar = (uyeler.data?.candidates ?? []).filter((u) => u.userId !== currentAssigneeUserId);

  const kirpilmis = reason.trim();
  const gerekceEksik = reasonRequired && kirpilmis.length < reasonMinLength;

  const gonder = useMutation({
    mutationFn: async () => reassign({ userId, ...(kirpilmis ? { reason: kirpilmis } : {}) }),
    onSuccess: async () => {
      toast.success(successMessage);
      await onDone();
      onClose();
    },
    /**
     * ⚠ Çakışmada diyalog KAPANMIYOR ve sunucunun cümlesi kutunun içinde kalıyor.
     * Toast'a düşürseydik, mesaj birkaç saniye sonra kaybolur ve kullanıcı elindeki
     * seçimi kaybetmeden neyin yanlış gittiğini okuyamazdı.
     */
    onError: (err: unknown) =>
      setCakisma(err instanceof Error ? err.message : fallbackErrorMessage),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="button"
            loading={gonder.isPending}
            disabled={!userId || gerekceEksik || gonder.isPending}
            onClick={() => gonder.mutate()}
            data-testid="yeniden-atama-onayla"
          >
            Devret
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {uyeler.isPending ? (
          <Skeleton lines={2} />
        ) : (
          <FormField label={assigneeLabel}>
            <Select
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setCakisma(null);
              }}
              data-testid="yeniden-atama-aday"
            >
              <option value="">Seçiniz</option>
              {adaylar.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.name}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <UyeListesiDurumu hata={uyeler.error} bosMu={!uyeler.isPending && adaylar.length === 0} />

        <FormField
          label={reasonRequired ? "Gerekçe" : "Gerekçe (isteğe bağlı)"}
          error={
            gerekceEksik && kirpilmis.length > 0
              ? `Gerekçe en az ${reasonMinLength} karakter olmalıdır`
              : undefined
          }
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Görevin neden devredildiğini yazın."
          />
        </FormField>

        {cakisma ? (
          <p role="alert" data-testid="yeniden-atama-hatasi" className="text-sm text-danger">
            {cakisma}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
